import { ampli } from '@/ampli';
import { useEffect, useState } from 'react';

import { NetworkFilter } from '@/components/Filters/NetworkFilter';

import { buttons } from '@/styles/buttons';

import { FileIcon } from '@/ui/Icons/FileIcon';
import { GridIcon } from '@/ui/Icons/GridIcon';
import { Multiselect } from '@/ui/Multiselect';

import {
  DiscoveryFilters,
  DiscoveryLaunchpad,
  DiscoveryPreset,
} from '../../../../utils/discovery/types';
import { BackingFilter } from './BackingFilter';
import { CreatorFilter } from './CreatorFilter';
import { CurrencyFilter } from './CurrencyFilter';
import { launchpadList, socialTypeList } from './filtersDefinitions';
import { IssuerFilter } from './IssuerFilter';
import { TimeframeFilter, TimeframeFilterProps } from './TimeframeFilter';

type CategoryFiltersProps = TimeframeFilterProps & {
  filters: DiscoveryFilters;
  preset: DiscoveryPreset | null;
  onFiltersChange: (filters: DiscoveryFilters) => void;
};

export const CategoryFilters = ({
  timeframe,
  filters,
  onFiltersChange,
  onTimeframeChange,
  preset,
}: CategoryFiltersProps) => {
  const [selectedNetworks, setSelectedNetworks] = useState(filters.networks.map(String));

  const initialSocialTypes = preset === 'social' ? filters.launchpads : undefined;
  const [selectedSocialTypes, setSelectedSocialTypes] = useState(initialSocialTypes);

  const initialLaunchpads = preset === 'launchpads' ? filters.launchpads : undefined;
  const [selectedLaunchpads, setSelectedLaunchpads] = useState(initialLaunchpads);

  const handleNetworkSelectionComplete = () => {
    const selectedNetworkFilters = selectedNetworks.map(Number);
    ampli.homepageNetworkFilterClicked({ selected_chain_ids: selectedNetworkFilters });
    onFiltersChange({ ...filters, networks: selectedNetworkFilters });
  };

  const handleFilterSelectionComplete = (
    selection: string[] | undefined,
    filterKey: keyof DiscoveryFilters,
    _filterName: string,
    _categoryName: string,
  ) => {
    const processedSelection = selection?.length ? selection : undefined;
    const filterValue = processedSelection ? processedSelection.join(', ') : 'none';

    ampli.categoryFilterSelected({
      filter_value: filterValue,
    });
    onFiltersChange({ ...filters, [filterKey]: processedSelection });
  };

  // Sync local state with state.filters
  useEffect(() => setSelectedNetworks(filters.networks.map(String)), [filters.networks]);
  // Sync social types or launchpads with state.filters.launchpads
  useEffect(() => {
    if (preset === 'launchpads') {
      setSelectedLaunchpads(filters.launchpads);
    } else if (preset === 'social') {
      setSelectedSocialTypes(filters.launchpads);
    }
  }, [preset, filters.launchpads]);

  const hasActiveSubFilters = (() => {
    switch (preset) {
      case 'currencies':
        return !!(
          filters.currencies?.length ||
          filters.issuers?.length ||
          filters.backings?.length
        );
      case 'commodities':
        return !!(filters.issuers?.length || filters.backings?.length);
      case 'stocks':
        return !!filters.backings?.length;
      case 'social':
        return !!(filters.launchpads?.length || filters.creator);
      case 'launchpads':
        return !!filters.launchpads?.length;
      default:
        return false;
    }
  })();

  const handleResetSubFilters = () => {
    const { currencies, issuers, backings, launchpads, creator, ...rest } = filters;
    onFiltersChange(rest);
  };

  // Only show network filter for categories without sub-filters
  if (preset == null || ['popular', 'lsts', 'wrapped'].includes(preset)) {
    return (
      <NetworkFilter
        value={selectedNetworks}
        onChange={setSelectedNetworks}
        onDropdownClose={handleNetworkSelectionComplete}
      />
    );
  }

  return (
    <div className="flex gap-2 shrink-0 items-center">
      <NetworkFilter
        value={selectedNetworks}
        onChange={setSelectedNetworks}
        onDropdownClose={handleNetworkSelectionComplete}
      />
      <div className="w-[1px] h-5 bg-token-alphalight-300" />
      <div className="flex items-center gap-2 h-full">
        {preset === 'trending' && (
          <TimeframeFilter
            timeframe={timeframe}
            category={preset}
            onTimeframeChange={onTimeframeChange}
          />
        )}
        {preset === 'social' && (
          <Multiselect
            items={socialTypeList}
            value={selectedSocialTypes}
            onChange={(values) => setSelectedSocialTypes(values as DiscoveryLaunchpad[])}
            onDropdownClose={() =>
              handleFilterSelectionComplete(selectedSocialTypes, 'launchpads', 'type', 'social')
            }
            modal={true}
            triggerVariant="secondary"
            label={{ default: 'Type', entity: 'types', Icon: GridIcon }}
            multiselect={true}
          />
        )}
        <CreatorFilter
          preset={preset}
          filters={filters}
          selectedSocialTypes={selectedSocialTypes}
          onFiltersChange={onFiltersChange}
        />
        {preset === 'launchpads' && (
          <Multiselect
            items={launchpadList}
            value={selectedLaunchpads}
            onChange={(values) => setSelectedLaunchpads(values as DiscoveryLaunchpad[])}
            onDropdownClose={() =>
              handleFilterSelectionComplete(
                selectedLaunchpads,
                'launchpads',
                'launchpad',
                'launchpads',
              )
            }
            showSelectAllButton={true}
            title="Launchpads"
            itemsLayout="two-columns"
            modal={true}
            triggerVariant="secondary"
            label={{ default: 'Launchpad', entity: 'launchpads', Icon: FileIcon }}
            multiselect={true}
          />
        )}
        {preset === 'currencies' && (
          <>
            <CurrencyFilter
              filters={filters}
              onSelectionComplete={(currencies) =>
                handleFilterSelectionComplete(currencies, 'currencies', 'currency', 'currencies')
              }
            />
            <IssuerFilter
              filters={filters}
              preset={preset}
              onSelectionComplete={(issuers) =>
                handleFilterSelectionComplete(issuers, 'issuers', 'issuer', 'currencies')
              }
            />
            <BackingFilter
              filters={filters}
              preset={preset}
              onSelectionComplete={(backings) =>
                handleFilterSelectionComplete(backings, 'backings', 'backing', 'currencies')
              }
            />
          </>
        )}
        {preset === 'commodities' && (
          <>
            <IssuerFilter
              filters={filters}
              preset={preset}
              onSelectionComplete={(issuers) =>
                handleFilterSelectionComplete(issuers, 'issuers', 'issuer', 'commodities')
              }
            />
            <BackingFilter
              filters={filters}
              preset={preset}
              onSelectionComplete={(backings) =>
                handleFilterSelectionComplete(backings, 'backings', 'backing', 'commodities')
              }
            />
          </>
        )}
        {preset === 'stocks' && (
          <BackingFilter
            filters={filters}
            preset={preset}
            onSelectionComplete={(backings) =>
              handleFilterSelectionComplete(backings, 'backings', 'underlying_asset', 'stocks')
            }
          />
        )}
        {hasActiveSubFilters && (
          <button
            onClick={handleResetSubFilters}
            className={buttons({
              size: 'small',
              shape: 'rounded',
              color: 'transparent',
            })}
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
};
